import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import UITextToolbarIcon from 'rich-text-lib/components/editor/UITextToolbarIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import hubHttpClient from 'hub-http/clients/apiClient';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import PortalIdParser from 'PortalIdParser';
import identity from 'transmute/identity';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { documentsApiWrapper } from './DocumentsApi';
import GenericPopoverErrorState from '../../components/GenericPopoverErrorState';
import { insertLink } from '../../utils/documentUtils';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import DocumentsPopoverEmptyState from './DocumentsPopoverEmptyState';
import ContentContainer from '../../components/insertMenu/ContentContainer';
import { getDocumentsUrl } from '../../utils/manageLinks';
import { getGlobalVars } from '../../utils/configUtils';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { jsx as _jsx } from "react/jsx-runtime";
const DocumentsButton = ({
  onClick
}) => /*#__PURE__*/_jsx(UITooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertDocuments"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "documents",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(UITextToolbarIcon, {
      name: "documents"
    })
  })
});
export default function InsertDocuments({
  onInsertContent = identity,
  onOpenPopover = identity,
  apiWrapper = documentsApiWrapper,
  apiClient = hubHttpClient,
  outputType = contentOutputs.RENDERED,
  Button = DocumentsButton,
  Header = GenericPopoverHeader,
  PopoverComponent = UIPopover,
  contactEmail,
  useContentContainer,
  onViewInsertMenu = () => {},
  onInsertComplete = () => {}
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertDocument = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete]);
  const DocumentsPopover = useMemo(() => {
    const apiFunctions = apiWrapper(apiClient, contactEmail);
    const insertDocumentsDecoratorConfig = {
      outputType,
      apiFunctions
    };
    if (useContentContainer) {
      return InsertContentDecorator(ContentContainer({
        onInsertContent,
        portalId: PortalIdParser.get(),
        PopoverErrorState: GenericPopoverErrorState,
        PopoverEmptyState: DocumentsPopoverEmptyState,
        Header,
        searchPlaceHolder: 'draftPlugins.documents.popover.search.searchPlaceholder',
        search: apiFunctions.searchArticles,
        onViewInsertMenu,
        // @ts-expect-error portalId can be undefined in unit testing for example
        manageUrl: getDocumentsUrl(PortalIdParser.get()),
        titleMessage: 'draftPlugins.documents.popover.header.insertDocument'
      }), insertDocumentsDecoratorConfig);
    }
    return InsertContentDecorator(ContentButton({
      onInsertContent,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button,
      PopoverErrorState: GenericPopoverErrorState,
      PopoverEmptyState: DocumentsPopoverEmptyState,
      PopoverComponent,
      Header,
      onOpenPopover,
      popoverClassName: 'prosemirror-documents-popover',
      searchPlaceHolder: 'draftPlugins.documents.popover.search.searchPlaceholder',
      search: apiFunctions.searchArticles,
      popoverTestKey: 'documents-popover',
      // @ts-expect-error portalId can be undefined in unit testing for example
      manageUrl: getDocumentsUrl(PortalIdParser.get()),
      titleMessage: 'draftPlugins.documents.popover.header.insertDocument'
    }), insertDocumentsDecoratorConfig);
  }, [onInsertContent, onOpenPopover, apiClient, apiWrapper, outputType, Button, PopoverComponent, Header, contactEmail, onViewInsertMenu, useContentContainer]);
  return /*#__PURE__*/_jsx(DocumentsPopover, {
    onChange: insertDocument,
    useProsemirror: true
  });
}